import { useReactiveVar } from "@apollo/client";
import React, { useEffect, useRef, useState } from "react";
import Joyride, { CallBackProps, STATUS, ACTIONS } from "react-joyride";
import { useNavigate } from "react-router-dom";
import useElementVisible from "../../../hooks/useElementVisible";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import {
  whiteLabelJRActive,
  whiteLabelJRStep,
} from "../../../qraphql/globalVar/state";
import { CustomTooltip } from "./tooltips/CustomTooltip";
import useSteps from "./useSteps";

export const JoyrideHits = () => {
  const lng = useGetUrlLocale();
  const whiteLabelJRActiveValue = useReactiveVar(whiteLabelJRActive);
  const whiteLabelJRStepValue = useReactiveVar(whiteLabelJRStep);
  const { steps } = useSteps();
  const [joyrideKey, setJoyrideKey] = useState(0);
  const { observeElement } = useElementVisible();
  const navigate = useNavigate();
  const observersRef = useRef<ResizeObserver[]>([]);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status } = data;
    if (index === 2 && action === ACTIONS.NEXT) {
      observeElement("seo-info", () => {
        whiteLabelJRActive(true);
      });
    } else if (index === 3 && action === ACTIONS.PREV) {
      (document.querySelector(".general-tab") as HTMLElement | null)?.click();
    } else if (index === 4 && !document.querySelector("#social-links")) {
      whiteLabelJRActive(false);
      observeElement("social-links", () => {
        whiteLabelJRActive(true);
      });
    } else if (index === 10 && !document.querySelector("#myDomain")) {
      whiteLabelJRActive(false);
      observeElement("myDomain", () => {
        whiteLabelJRActive(true);
      });
    } else if (index === 10 && action === ACTIONS.NEXT) {
      navigate(`${lng}/media-design`);
    } else if (index === 6 && action === ACTIONS.NEXT) {
      whiteLabelJRStep(11);
      navigate(`${lng}/media-design`);
    } else if (index === 5 && !document.querySelector("#myDomain")) {
      whiteLabelJRActive(false);
      observeElement("myDomain", () => {
        whiteLabelJRActive(true);
      });
    }

    if (index === 3 && status === STATUS.SKIPPED) {
      (
        document.querySelector(".social-links-tab") as HTMLElement | null
      )?.click();
      whiteLabelJRActive(false);
      whiteLabelJRStep(4);
      observeElement("social-links", () => {
        whiteLabelJRActive(true);
      });
    }
  };

  // Observe size changes of the current Joyride step's target element.
  // If the element's size changes, force a re-render of Joyride to update the spotlight.
  // Includes debouncing and size checks to avoid infinite ResizeObserver loops.
  useEffect(() => {
    if (!whiteLabelJRActiveValue) return;
    observersRef.current.forEach((o) => o.disconnect());
    observersRef.current = [];

    steps.forEach((step, index) => {
      if (typeof step.target !== "string") return;

      const element = document.querySelector(step.target) as HTMLElement | null;
      if (!element) return;

      let lastWidth = element.offsetWidth;
      let lastHeight = element.offsetHeight;

      const observer = new ResizeObserver(() => {
        if (whiteLabelJRStepValue !== index) return;
        if (
          element.offsetWidth === lastWidth &&
          element.offsetHeight === lastHeight
        ) {
          return;
        }

        lastWidth = element.offsetWidth;
        lastHeight = element.offsetHeight;

        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }

        resizeTimeoutRef.current = setTimeout(() => {
          setJoyrideKey((prev) => prev + 1);
        }, 100);
      });

      observer.observe(element);
      observersRef.current.push(observer);
    });

    return () => {
      observersRef.current.forEach((o) => o.disconnect());
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [steps, whiteLabelJRStepValue, whiteLabelJRActiveValue]);

  return (
    <>
      <Joyride
        key={joyrideKey}
        steps={steps}
        run={whiteLabelJRActiveValue}
        stepIndex={whiteLabelJRStepValue}
        // debug={true}
        scrollToFirstStep={true}
        disableOverlayClose={true}
        callback={handleJoyrideCallback}
        tooltipComponent={CustomTooltip}
        styles={{
          options: {
            zIndex: 10000,
            overlayColor: "rgba(0, 0, 0, 0.3)",
          },
        }}
      />
    </>
  );
};
